import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import { useTheme } from "@mui/material/styles";
import { useLocation } from "react-router-dom";

import Container from "@mui/material/Container";

import { useOffSetTop } from "src/hooks/use-off-set-top";
import { useResponsive } from "src/hooks/use-responsive";

import { bgBlur } from "src/theme/css";

import Logo from "src/components/logo";

import Iconify from "src/components/iconify";

import NavMobile from "./nav/mobile";
import NavDesktop from "./nav/desktop";
import { HEADER } from "../config-layout";
import { navConfig } from "./config-navigation";
import { getPlayerNavConfig } from "./config-player-navigation";

import HeaderShadow from "../common/header-shadow";
import { useMemo, useState } from "react";
import AccountPopover from "../common/account-popover";
import NotificationsPopover from "../common/notifications-popover";
import RegistrationDialog from "src/components/custom-dialog/registration-dialog";
import LoginDialog from "src/components/custom-dialog/login-dialog";

import { useAuthContext } from "src/auth/hooks";
import CartIcon from "src/sections/cart/cart-icon";

import { useWhiteLabel } from "src/context/WhiteLabelContext";
import { useGetMyTeams } from "src/api/team";
import { usePlayerFinanceNavigation } from "src/api/federation-finance";
// ----------------------------------------------------------------------

export default function Header() {
  const { authenticated, user } = useAuthContext();
  const { hasFederationMembership } = usePlayerFinanceNavigation(
    authenticated ? user?.id : undefined,
  );
  const location = useLocation();

  const theme = useTheme();

  const mdUp = useResponsive("up", "md");

  const offsetTop = useOffSetTop(HEADER.H_DESKTOP);
  const [open, setOpen] = useState(true); // State to control alert visibility
  const { hideHeaderMenu, backgroundColor, headerMenuItems, isCustomHost } =
    useWhiteLabel();

  const {
    myTeams,
    myTeamsLoading,
    myTeamsError,
    myTeamsEmpty,
    refetchMyTeams,
  } = useGetMyTeams(user?.id);

  const filteredPlayerNavConfig = useMemo(() => {
    return getPlayerNavConfig(hasFederationMembership).filter((item) => {
      if (item.title === "playermenu_teams") {
        return !myTeamsEmpty;
      }
      return true;
    });
  }, [myTeamsEmpty, hasFederationMembership]);

  const finalNavConfig = headerMenuItems?.length
    ? mapJsonToNavConfig(headerMenuItems)
    : navConfig;

  return (
    <>
      <AppBar
        sx={{
          boxShadow: theme.customShadows.z4,
          background: backgroundColor,
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            height: {
              xs: HEADER.H_MOBILE,
              md: HEADER.H_DESKTOP,
            },
            backgroundColor: theme.palette.background.default,
            transition: theme.transitions.create(["height"], {
              easing: theme.transitions.easing.easeInOut,
              duration: theme.transitions.duration.shorter,
            }),
            ...(offsetTop && {
              ...bgBlur({
                color: theme.palette.background.default,
              }),
              height: {
                xs: HEADER.H_MOBILE_OFFSET,
                md: HEADER.H_DESKTOP_OFFSET,
              },
            }),
          }}
        >
          <Container sx={{ height: 1, display: "flex", alignItems: "center" }}>
            <Logo />
            {authenticated && <CartIcon />}
            <Box sx={{ flexGrow: 0.3 }} />

            {!hideHeaderMenu && mdUp && <NavDesktop data={finalNavConfig} />}

            <Box sx={{ flexGrow: 0.7 }} />

            <Stack alignItems="center" direction="row-reverse">
              {!isCustomHost && !authenticated && <RegistrationDialog />}

              <LoginDialog
                returnTo={
                  isCustomHost
                    ? `${location.pathname}${location.search}${location.hash}`
                    : undefined
                }
              />

              {!hideHeaderMenu && !mdUp && <NavMobile data={finalNavConfig} />}
            </Stack>
            <Stack alignItems="center" direction="row" spacing={1}>
              {authenticated && <NotificationsPopover />}
              {authenticated && (
                <AccountPopover options={filteredPlayerNavConfig} />
              )}
            </Stack>
          </Container>
        </Toolbar>
        {offsetTop && <HeaderShadow />}
      </AppBar>
    </>
  );
}

export function mapJsonToNavConfig(headerMenuItems: any[]): any[] {
  return headerMenuItems.map((item) => ({
    title: item.title,
    path: item.path,
    icon: <Iconify icon={item.icon || "solar:circle-bold"} />,
  }));
}
